import { useState, useEffect } from 'react';
import { isSameBuilding } from '../../../utils/addressSharing';
import { findSharedFamilyComposition } from '../../../utils/familyComposition';
import { INBOEDEL_INFOFOLIO_FIELDS_KEY, glassCoveredByOtherProduct } from '../../../utils/glassPremium';
import { useSharedFamilyComposition } from '../../../hooks/useSharedFamilyComposition';

// Owns the upstream-sync state moved verbatim from InboedelScreen: the shared family
// composition (single source across products, mirrored into IP_GSS), the three lock states
// (address / building details / Gezinssamenstelling, seeded from Woonhuis) incl. the mount
// restore effect, and the Glas-force-off effect (glass is already covered by a selected
// product from config.glassPremium.coveredByProducts, default Woonhuis).
// The VP-input seeding effect and the big sharedAddress sync effect stay in the screen —
// their wiring (address input setters, pendingInfofolioAddress, isEditingAddress ref, ...)
// exceeds a sane param count; they move once the address plumbing is componentized.

interface InboedelUpstreamSyncArgs {
  screenId: string;
  data: any;
  setData: (updater: any) => void;
  getStepData: (id: string) => any;
  saveStepData: (id: string, data: any) => void;
  selectedProducts: any;
  config: any;
  glassDisabled?: boolean;
  setInfofolioFilledFields: React.Dispatch<React.SetStateAction<Set<string>>>;
  clearInfofolioTracking: () => void; // useInfofolio's state+storage reset (fields, address, done key)
  handleCustomFieldChange: (fieldPath: string, value: any) => void;
}

export function useUpstreamSync({
  screenId,
  data,
  setData,
  getStepData,
  saveStepData,
  selectedProducts,
  config,
  glassDisabled,
  setInfofolioFilledFields,
  clearInfofolioTracking,
  handleCustomFieldChange,
}: InboedelUpstreamSyncArgs) {
  // Single source of truth for the family composition across every product.
  const { familyComposition, setFamilyComposition } = useSharedFamilyComposition();

  // Migrate a value entered before the shared store existed / on a since-deselected product.
  useEffect(() => {
    if (!familyComposition) {
      const found = findSharedFamilyComposition(getStepData);
      if (found) setFamilyComposition(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyComposition]);

  // Mirror the shared value into IP_GSS so the BFF payload carries it.
  useEffect(() => {
    if (!familyComposition) return;
    setData((prev: any) => {
      if (prev?.Contractdocument?.PP?.IP?.IP_GSS === familyComposition) return prev;
      const updated = {
        ...prev,
        Contractdocument: {
          ...prev?.Contractdocument,
          PP: {
            ...prev?.Contractdocument?.PP,
            IP: { ...prev?.Contractdocument?.PP?.IP, IP_GSS: familyComposition },
          },
        },
      };
      setTimeout(() => saveStepData(screenId, updated), 0);
      return updated;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyComposition]);

  // Address is editable everywhere — changes propagate to every other selected product
  // via syncAddressToOtherProducts.
  const [isAddressLocked, setIsAddressLocked] = useState(false);

  // Check if building details are locked (filled in Woonhuis, which comes before Inboedel)
  const [isBuildingDetailsLocked, setIsBuildingDetailsLocked] = useState(() => {
    const woonhuisBA = getStepData('woonhuis')?.Contractdocument?.PP?.BA;
    const inboedelBA = getStepData('inboedel')?.Contractdocument?.PP?.BA;
    return Boolean(selectedProducts?.woonhuis && woonhuisBA?.BA_TYPEWON && isSameBuilding(woonhuisBA, inboedelBA));
  });

  // Check if Gezinssamenstelling is locked (filled in Woonhuis, which comes before Inboedel)
  const [isGezinsamenstellingLocked, setIsGezinsamenstellingLocked] = useState(() => {
    const woonhuisData = getStepData('woonhuis');
    const hasGezinssamenstelling = woonhuisData?.Contractdocument?.PP?.OP?.OP_GSS;
    return Boolean(selectedProducts?.woonhuis && hasGezinssamenstelling);
  });

  // Restore locked states and infofolio fields on mount (in case useState initializer didn't run)
  useEffect(() => {
    // Check if there's a valid address before restoring infofolio fields
    const currentData = getStepData('inboedel');
    const woonhuisData = getStepData('woonhuis');
    const avpData = getStepData('aansprakelijkheid');

    // Get BA from any available source
    const ba = currentData?.Contractdocument?.PP?.BA ||
               woonhuisData?.Contractdocument?.PP?.BA ||
               avpData?.Contractdocument?.PP?.BA;
    const hasValidAddress = ba?.BA_PCODE && ba?.BA_HUISNR;

    // Only restore infofolioFilledFields if there's a valid address
    if (hasValidAddress) {
      try {
        const saved = sessionStorage.getItem(INBOEDEL_INFOFOLIO_FIELDS_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed) && parsed.length > 0) {
            const set = new Set<string>(parsed);
            // Migration: older sessions may have been saved before 'monumen' was tracked.
            set.add('monumen');
            set.delete('permbew'); // older sessions locked it; Infofolio never answers it
            setInfofolioFilledFields(set);
          }
        }
      } catch (e) {
        // Ignore parse errors
      }
    } else {
      // No valid address - clear any stale infofolio state (state AND storage, all three keys)
      clearInfofolioTracking();
    }

    // Address is editable everywhere — never lock it.
    setIsAddressLocked(false);
    const woonhuisDataRestore = getStepData('woonhuis');

    // Restore isBuildingDetailsLocked (from Woonhuis) — only when it's the same building.
    const woonhuisBARestore = woonhuisDataRestore?.Contractdocument?.PP?.BA;
    const inboedelBARestore = getStepData('inboedel')?.Contractdocument?.PP?.BA;
    setIsBuildingDetailsLocked(Boolean(selectedProducts?.woonhuis && woonhuisBARestore?.BA_TYPEWON && isSameBuilding(woonhuisBARestore, inboedelBARestore)));

    // Restore isGezinsamenstellingLocked (from Woonhuis)
    const hasGezinssamenstelling = woonhuisDataRestore?.Contractdocument?.PP?.OP?.OP_GSS;
    setIsGezinsamenstellingLocked(Boolean(selectedProducts?.woonhuis && hasGezinssamenstelling));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  const glassCovered = Boolean(glassDisabled) || glassCoveredByOtherProduct(selectedProducts, config);
  useEffect(() => {
    if (!glassCovered) return;
    if (data?.Supportsection?.Dekkingen?.Glas === 'J') {
      handleCustomFieldChange('Supportsection.Dekkingen.Glas', 'N');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [glassCovered, data?.Supportsection?.Dekkingen?.Glas]);

  return {
    familyComposition,
    setFamilyComposition,
    isAddressLocked,
    setIsAddressLocked,
    isBuildingDetailsLocked,
    setIsBuildingDetailsLocked,
    isGezinsamenstellingLocked,
    setIsGezinsamenstellingLocked,
  };
}
